<script lang="ts" setup>
import {computed} from 'vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import {ScreenSize} from '@/ts/types/component/screen-size.type';

const modal = defineModel<boolean>();
defineSlots<{
  default: [];
}>();

const props = withDefaults(defineProps<{
  type?: 'fh' | 'fit-content' | 'h900';
  theme?: 'grey-10' | 'grey-20' | 'neutral-0';
  noX?: boolean;
  noScroll?: boolean;
  noPadding?: boolean;
  blurBackdrop?: boolean;
  ariaLabel?: string;
  ariaLabelledby?: string;
  xCloseColor?: string;
  // By default the dialog uses the bottom-sheet presentation below `lg` (1280px),
  // i.e. on tablets too. When set, only phones (XS/SM) get the bottom-sheet and
  // tablets (MD) get the centered desktop presentation.
  desktopFromTablet?: boolean;
}>(), {
  type: 'fh',
  theme: 'grey-20',
  noX: false,
  noScroll: false,
  noPadding: false,
  blurBackdrop: false,
  desktopFromTablet: false,
});

const {isMobileResolution, screenSize} = useScreenSize();

const isBottomSheet = computed<boolean>(() => (props.desktopFromTablet
  ? [ScreenSize.XS, ScreenSize.SM].includes(screenSize.value)
  : isMobileResolution.value));

</script>
<template>
  <q-dialog
    v-model="modal"
    :class="['balm-dialog', {'balm-dialog--fit-content': type === 'fit-content', 'balm-dialog--blur-backdrop': blurBackdrop}]"
    :persistent="!noX"
    :transition-show="isBottomSheet ? 'slide-up' : 'jump-up'"
    :transition-hide="isBottomSheet ? 'slide-down' : 'jump-down'"
    :position="isBottomSheet ? 'bottom' : 'standard'"
    :aria-label="ariaLabelledby ? null : (ariaLabel || $t('aria_dialog'))"
    :aria-labelledby="ariaLabelledby"
  >
    <div class="modal-card" :class="[type, theme, {'desktop-from-tablet': desktopFromTablet}]">
      <slot v-if="noScroll"/>
      <div v-else class="modal-scroll" :class="{'no-padding': props.noPadding}">
        <slot/>
      </div>
      
      <div class="x-close-wrapper">
        <flat-icon-btn
          v-if="!noX"
          class="x-close"
          icon="x-close.svg"
          :label="$t('aria_close_modal')"
          :style="xCloseColor ? {color: xCloseColor} : undefined"
          @click="modal = false"
        />
      </div>
    </div>
  </q-dialog>
</template>
<style lang="scss" scoped>
.x-close-wrapper {
  position: absolute;
  right: var(--Spacing-spacing-6);
  top: var(--Spacing-spacing-6);
  background-color: inherit;
  border-radius: 50%;
}

.modal-scroll {
  overflow-y: scroll;
  overflow-x: hidden;
  padding: var(--Spacing-spacing-4);
  min-height: 100%;

  &.no-padding {
    padding: 0;
  }
}

/* eslint-disable-next-line vue-scoped-css/no-unused-selector */
.modal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1800px;
  overflow: hidden;
  border-top-left-radius: var(--card-border-radius);
  border-top-right-radius: var(--card-border-radius);

  &.grey-10 {
    background-color: var(--Neutral-UI-neutral-ui-10);
  }

  &.grey-20 {
    background-color: var(--Neutral-UI-neutral-ui-20);
  }

  &.neutral-0 {
    background-color: var(--Neutral-UI-neutral-ui-0);
  }
  &.h900 {
      height: 100%;
      max-height: min(90vh, 1100px) !important;
      max-height: min(90dvh, 1100px) !important;

      @include screen-lg {
        max-height: min(100vh, 1100px) !important;
        max-height: min(100dvh, 1100px) !important;
      }
    }

  &.fit-content {
    height: fit-content;
  }

  @include screen-lg {

    border-bottom-left-radius: var(--card-border-radius);
    border-bottom-right-radius: var(--card-border-radius);


    &.fit-content {
      width: fit-content;
      height: fit-content;
    }

    &.fh {
      height: 100%;
    }
  }

  // Bring the centered desktop presentation forward to the tablet breakpoint.
  &.desktop-from-tablet {
    @include screen-md {
      border-bottom-left-radius: var(--card-border-radius);
      border-bottom-right-radius: var(--card-border-radius);

      &.fit-content {
        width: fit-content;
        height: fit-content;
      }

      &.fh {
        height: 100%;
      }
    }
  }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
// unscoped styles !!!
// override quasar behaviour
// deep doesn't work because of the portal
// all styles hear should be wrapped in the .balm-dialog class

.balm-dialog {
  .q-dialog__inner{

    height: 90vh;
    height: 90dvh;

    @include screen-md {
      height: 90vh;
      height: 90dvh;
    }

    @include screen-lg {
      height: 100vh;
      height: 100dvh;
    }

    &--minimized {
      padding: 0;

      @include screen-lg {
        padding: var(--Spacing-spacing-7);
      }
    }
    & > div {
      // The frame above is 90vh here, and this was 100vh: a card with more content than fits grew
      // taller than its own frame, and the strip that overflowed sat under the bottom of the
      // window with nothing left to scroll — on the recap, the end of the session list. Matched to
      // the frame rather than written as a percentage of it, because a `fit-content` dialog gives
      // this element an auto-height parent, against which a percentage is indefinite and caps
      // nothing at all.
      max-height: 90vh !important;
      max-height: 90dvh !important;

      @include screen-md {
        max-height: 90vh !important;
        max-height: 90dvh !important;
      }

      @include screen-lg {
        max-height: 100% !important;
      }
    }
  }

  &--fit-content {
    .q-dialog__inner {
      height: auto;
    }
  }

  &--blur-backdrop {
    .q-dialog__backdrop {
      backdrop-filter: blur(6px);
      // stylelint-disable-next-line property-no-vendor-prefix
      -webkit-backdrop-filter: blur(6px);
    }
  }
}

</style>
